import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle, ImagePlus, X, Camera, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AfterPhotoUpload() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [afterImages, setAfterImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [galleryPermission, setGalleryPermission] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const { data } = await axios.get(`${API}/quote/upload/${token}`);
        setQuote(data);
        if (data.after_images && data.after_images.length > 0) {
          setSubmitted(true);
        }
      } catch {
        setError("Deze link is ongeldig of verlopen.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [token]);

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = 10 - afterImages.length;
    const toUpload = files.slice(0, remaining);
    if (toUpload.length === 0) return;

    setIsUploading(true);
    try {
      const fd = new FormData();
      toUpload.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/upload`, fd);
      const newImgs = data.files.map((f) => ({
        url: f.url,
        preview: `${process.env.REACT_APP_BACKEND_URL}${f.url}`,
      }));
      setAfterImages((prev) => [...prev, ...newImgs]);
    } catch {
      // silent
    } finally {
      setIsUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (afterImages.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote/upload/${token}`, {
        after_images: afterImages.map((img) => img.url),
        gallery_permission: galleryPermission,
      });
      setSubmitted(true);
    } catch {
      setError("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="text-center">
          <X size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Link ongeldig</h2>
          <p className="text-[#A1A1AA]">{error}</p>
        </div>
      </div>
    );
  }

  const REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJ0_gtudJBwUcRtS9-JeYoNOI";

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <CheckCircle size={64} className="mx-auto text-[#4DD0E1] mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Bedankt!</h2>
          <p className="text-[#A1A1AA] mb-4">
            Uw na-foto's zijn succesvol geüpload. {galleryPermission ? "Ze worden binnenkort zichtbaar op onze website." : ""}
          </p>

          <div className="mt-8 p-6 rounded-2xl bg-[#121212] border border-white/10">
            <Star size={32} className="mx-auto text-[#FBBC04] mb-3" />
            <h3 className="text-white font-bold mb-2">Tevreden met het resultaat?</h3>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Een Google Review helpt ons enorm en helpt andere klanten de juiste keuze te maken.
            </p>
            <a
              href={REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#FBBC04] text-black font-bold hover:bg-[#e5ab00] transition-colors"
            >
              <Star size={18} /> Google Review Schrijven
            </a>
          </div>

          <a href="/" className="text-[#4DD0E1] hover:underline text-sm mt-6 inline-block">Terug naar Clean-Force.eu</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-16 px-6">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-10">
          <img src="/logo-clean-force.png" alt="Clean-Force" className="h-8 mx-auto mb-6" />
          <Camera size={40} className="mx-auto text-[#4DD0E1] mb-3" />
          <h1 className="text-2xl font-bold text-white mb-2">Na-foto uploaden</h1>
          <p className="text-[#A1A1AA] text-sm">
            Beste {quote?.name}, deel het resultaat van uw <strong className="text-white">{quote?.service_type}</strong> schoonmaak!
          </p>
        </div>

        {quote?.before_images?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-[#A1A1AA] mb-3">Voor-foto's</h3>
            <div className="flex flex-wrap gap-2">
              {quote.before_images.map((img, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="Voor" className="w-24 h-24 object-cover rounded-lg border border-white/10" />
              ))}
            </div>
          </div>
        )}

        <div className="p-6 rounded-2xl bg-[#121212] border border-white/10">
          <h3 className="text-white font-medium mb-4">
            <ImagePlus size={16} className="inline mr-2" />
            Upload uw na-foto's (max 10)
          </h3>

          <input
            type="file"
            ref={fileRef}
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleImageSelect}
            data-testid="after-photo-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={isUploading || afterImages.length >= 10}
            data-testid="after-photo-upload-btn"
            className="w-full py-6 rounded-xl border-2 border-dashed border-white/10 hover:border-[#4DD0E1]/40 text-[#A1A1AA] hover:text-[#4DD0E1] transition-colors disabled:opacity-30 flex items-center justify-center gap-2"
          >
            {isUploading ? (
              <><div className="w-5 h-5 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" /> Uploaden...</>
            ) : (
              <><ImagePlus size={24} /> Klik om foto's te selecteren</>
            )}
          </button>

          {afterImages.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {afterImages.map((img, i) => (
                <div key={i} className="relative group">
                  <img src={img.preview} alt="" className="w-20 h-20 object-cover rounded-lg border border-white/20" />
                  <button
                    onClick={() => setAfterImages((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="flex items-center gap-3 mt-6 p-4 rounded-xl bg-white/5 cursor-pointer hover:bg-white/10 transition-colors" data-testid="gallery-permission-label">
            <input
              type="checkbox"
              checked={galleryPermission}
              onChange={(e) => setGalleryPermission(e.target.checked)}
              className="w-5 h-5 rounded border-white/20 text-[#4DD0E1] focus:ring-[#4DD0E1]"
              data-testid="gallery-permission-checkbox"
            />
            <span className="text-sm text-[#A1A1AA]">
              Ik geef toestemming om de voor/na vergelijking op de Clean-Force website te tonen
            </span>
          </label>

          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={afterImages.length === 0 || submitting}
            data-testid="after-photo-submit"
            className="w-full mt-6 py-3 rounded-xl bg-[#4DD0E1] text-black font-bold hover:bg-[#3bc0d1] transition-colors disabled:opacity-50"
          >
            {submitting ? "Verzenden..." : `${afterImages.length} foto('s) versturen`}
          </button>
        </div>
      </div>
    </div>
  );
}
